"use client"

import { useState, useEffect, useContext } from "react"
import DashboardLayout from "../components/Layout/DashboardLayout"
import api from "../services/api"
import { AuthContext } from "../context/AuthContext"
import MiniFooter from "../components/Layout/miniFooter"
import WhatsAppChat from "../components/support/WhatsAppChat"

export default function DashboardPage() {
  const { logout } = useContext(AuthContext)

  const [user, setUser] = useState(null)
  const [plans, setPlans] = useState(null)
  const [usage, setUsage] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, planRes, subscriptionRes, usageLim] = await Promise.all([
          api.get("/user/me"),
          api.get("/subscription/plans"),
          api.get("/subscription/status"),
          api.get("/links/analytics/user-analytics"),
        ])
        setUser(userRes.data.data)
        setUsage(usageLim.data.data)
        setPlans(planRes.data)
        setSubscription(subscriptionRes.data.currentPlan)
      } catch (err) {
        console.error(err)
        logout()
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [logout])
  
  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ padding: "32px", maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ marginBottom: "32px" }}>
            <div
              style={{
                height: "36px",
                width: "280px",
                background: "#E5E7EB",
                borderRadius: "8px",
                marginBottom: "12px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
            <div
              style={{
                height: "20px",
                width: "320px",
                background: "#F3F4F6",
                borderRadius: "6px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </div>
          <div className="row g-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "24px",
                    height: "160px",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </DashboardLayout>
    )
  }

  const activePlan = plans[user.plan]
  const urlUsagePercent = (user.linkCount / activePlan.limit) * 100
  const clickUsagePercent = ((usage.totalClicks || 0) / activePlan.trafficLimit) * 100
const billing = subscription?.billing || activePlan?.billing || "monthly";

const billingLabel = billing === "yearly" ? "Yearly Cost" : "Monthly Cost";
const billingSuffix = billing === "yearly" ? "/yr" : "/mo";

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: "#DCFCE7", color: "#166534", border: "#BBF7D0" },
      cancelled: { bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" },
      canceled: { bg: "#FEF3C7", color: "#92400E", border: "#FDE68A" },
      expired: { bg: "#FEE2E2", color: "#991B1B", border: "#FECACA" },
      inactive: { bg: "#FEE2E2", color: "#991B1B", border: "#FECACA" },
    }
    const s = styles[status?.toLowerCase()] || { bg: "#F3F4F6", color: "#374151", border: "#E5E7EB" }
    return {
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
    }
  }

  const cardStyle = {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    height: "100%",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  }

  const cardHover = (e, isEnter) => {
    e.currentTarget.style.borderColor = isEnter ? "#3B82F6" : "#E5E7EB"
    e.currentTarget.style.boxShadow = isEnter ? "0 4px 12px rgba(59, 130, 246, 0.1)" : "none"
  }

  const labelStyle = {
    color: "#6B7280",
    fontSize: "12px",
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "6px",
  }

  return (
    <DashboardLayout>
      <div style={{ background: "#F9FAFB", minHeight: "100vh" }}>
        <div className="container-fluid" style={{ maxWidth: "1200px", padding: "32px 24px" }}>
          <div style={{ marginBottom: "32px" }}>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#111827", marginBottom: "4px" }}>
              Welcome back, {user.userName}
            </h1>
            <p style={{ color: "#6B7280", fontSize: "15px", margin: 0 }}>Here's an overview of your account</p>
          </div>

          {/* Stats Cards */}
          <div className="row g-3 mb-4">
            {/* Plan Card */}
            <div className="col-lg-4 col-md-6">
              <div style={cardStyle} onMouseEnter={(e) => cardHover(e, true)} onMouseLeave={(e) => cardHover(e, false)}>
                <div style={{ padding: "20px" }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "#EEF2FF",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </div>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        ...getStatusBadge("active"),
                      }}
                    >
                      Active
                    </span>
                  </div>
                  <div style={labelStyle}>Current Plan</div>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#111827", textTransform: "capitalize" }}>
                    {user.plan}
                  </div>
                </div>
              </div>
            </div>

            {/* URLs Card */}
            <div className="col-lg-4 col-md-6">
              <div style={cardStyle} onMouseEnter={(e) => cardHover(e, true)} onMouseLeave={(e) => cardHover(e, false)}>
                <div style={{ padding: "20px" }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "#F0F9FF",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284C7" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    </div>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: urlUsagePercent > 80 ? "#FEF3C7" : "#F0F9FF",
                        color: urlUsagePercent > 80 ? "#92400E" : "#0369A1",
                      }}
                    >
                      {urlUsagePercent.toFixed(0)}% used
                    </span>
                  </div>
                  <div style={labelStyle}>URLs Created</div>
                  <div className="d-flex align-items-baseline gap-2 mb-3">
                    <span style={{ fontSize: "28px", fontWeight: "700", color: "#111827" }}>{user.linkCount}</span>
                    <span style={{ fontSize: "14px", color: "#9CA3AF" }}>/ {activePlan.limit}</span>
                  </div>
                  <div style={{ height: "6px", background: "#E5E7EB", borderRadius: "3px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${Math.min(urlUsagePercent, 100)}%`,
                        height: "100%",
                        background: urlUsagePercent > 80 ? "#F59E0B" : "#3B82F6",
                        borderRadius: "3px",
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Clicks Card */}
            <div className="col-lg-4 col-md-6">
              <div style={cardStyle} onMouseEnter={(e) => cardHover(e, true)} onMouseLeave={(e) => cardHover(e, false)}>
                <div style={{ padding: "20px" }}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        background: "#F0FDF4",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                      </svg>
                    </div>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: clickUsagePercent > 80 ? "#FEF3C7" : "#F0FDF4",
                        color: clickUsagePercent > 80 ? "#92400E" : "#166534",
                      }}
                    >
                      {clickUsagePercent.toFixed(0)}% used
                    </span>
                  </div>
                  <div style={labelStyle}>Total Traffics</div>
                  <div className="d-flex align-items-baseline gap-2 mb-3">
                    <span style={{ fontSize: "28px", fontWeight: "700", color: "#111827" }}>{usage.totalClicks || 0}</span>
                    <span style={{ fontSize: "14px", color: "#9CA3AF" }}>/ {activePlan.trafficLimit}</span>
                  </div>
                  <div style={{ height: "6px", background: "#E5E7EB", borderRadius: "3px", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${Math.min(clickUsagePercent, 100)}%`,
                        height: "100%",
                        background: clickUsagePercent > 80 ? "#F59E0B" : "#22C55E",
                        borderRadius: "3px",
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile & Billing Cards */}
          <div className="row g-3 mb-4">
            {/* Profile Card */}
            <div className="col-lg-6">
              <div style={cardStyle} onMouseEnter={(e) => cardHover(e, true)} onMouseLeave={(e) => cardHover(e, false)}>
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      background: "#EEF2FF",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <span style={{ fontSize: "15px", fontWeight: "600", color: "#111827" }}>Profile Information</span>
                </div>
                <div style={{ padding: "20px" }}>
                  <div className="row g-4">
                    <div className="col-6">
                      <div style={labelStyle}>Full Name</div>
                      <div style={{ fontSize: "15px", color: "#111827", fontWeight: "500" }}>{user.fullName}</div>
                    </div>
                    <div className="col-6">
                      <div style={labelStyle}>Email</div>
                      <div style={{ fontSize: "15px", color: "#111827", overflow: "hidden"  }}>{user.email}</div>
                    </div>
                    <div className="col-6">
                      <div style={labelStyle}>Company</div>
                      <div style={{ fontSize: "15px", color: "#111827" }}>{user.company || "—"}</div>
                    </div>
                    <div className="col-6">
                      <div style={labelStyle}>Member Since</div>
                      <div style={{ fontSize: "15px", color: "#111827" }}>
                        {new Date(user.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Card */}
            <div className="col-lg-6">
              <div style={cardStyle} onMouseEnter={(e) => cardHover(e, true)} onMouseLeave={(e) => cardHover(e, false)}>
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      background: "#F0FDF4",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                  </div>
                  <span style={{ fontSize: "15px", fontWeight: "600", color: "#111827" }}>Billing & Subscription</span>
                </div>
                <div style={{ padding: "20px" }}>
                  <div className="row g-4">
                    <div className="col-6">
                      <div style={labelStyle}>Status</div>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          textTransform: "capitalize",
                          ...getStatusBadge(subscription?.status || "active"),
                        }}
                      >
                        {subscription?.status || "Active"}
                      </span>
                    </div>
<div className="col-6">
  <div style={labelStyle}>{billingLabel}</div>
  <div style={{ fontSize: "15px", color: "#111827", fontWeight: "600" }}>
    ${subscription?.amount || activePlan?.price || 0}
    {billingSuffix}
  </div>
</div>

                    <div className="col-6">
                      <div style={labelStyle}>Current Period</div>
                      <div style={{ fontSize: "15px", color: "#111827" }}>
                        {subscription?.startDate && subscription?.endDate ? (
                          <>
                            {new Date(subscription.startDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                            {" — "}
                            {new Date(subscription.endDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </>
                        ) : (
                          "—"
                        )}
                      </div>
                    </div>
                    <div className="col-6">
                      <div style={labelStyle}>Next Billing</div>
                      <div style={{ fontSize: "15px", color: "#111827" }}>
                        {subscription?.nextBillingDate
                          ? new Date(subscription.nextBillingDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                          : subscription?.endDate
                            ? new Date(subscription.endDate).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "14px 18px",
              background: "#F3F4F6",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span style={{ color: "#4B5563", fontSize: "14px" }}>
              Usage data is updated every hour. Your plan limits reset on the 1st of each month.
            </span>
          </div>
        </div>
      </div>
      <WhatsAppChat position="right"/>
      <MiniFooter />
    </DashboardLayout>
  )
}
